// Gráfico da curva de desembolso Ampli DENTRO da tela de Desembolso.
//
// A FONTE é a série do projeto inteiro que `getDetail` publica na seleção da obra
// (`plannedCashFlowSeries.ampliDisbursement`), nunca `resolveAmpliSeries`/
// `project.ampliDisbursements`: esse segundo campo é a série recortada pelo período que
// o `DateRangeFilter` da Projeção Financeira deixou aplicado (`getFollowUps`) e sobrevive
// à troca de tela — usá-lo faria o gráfico herdar, calado, o recorte de uma visita
// anterior à Projeção.
//
// O RECORTE é o período escolhido aqui (`DisbursementPeriodFilter` → `filterDates`), o
// mesmo que a tabela da EAP usa para montar as colunas: gráfico e tabela mostram
// sempre a mesma janela. Nenhuma rota nova. O vínculo com a Projeção Financeira é
// preservado à parte, em `AmpliDisbursementCoverageCard` ("Abrir o módulo Desembolso").
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";

import { useProjectContext } from "@contexts/projects";

import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
  isAmpliDisbursementPolicy,
} from "../financialProjection/cashFlowPlanning";
import monthsBetween from "./months";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const PLAIN_UNAVAILABLE_HINT =
  "Esta obra ainda não tem previsto canônico para gerar a curva.";

const STATE_DETAILS = Object.freeze({
  stale: "A curva anterior continua no ar enquanto a nova é gerada.",
  unavailable: PLAIN_UNAVAILABLE_HINT,
});

/** A série do projeto inteiro, nunca a recortada por período — ver comentário do topo. */
const fullProjectAmpliSeries = (project) => {
  const series = project?.plannedCashFlowSeries?.ampliDisbursement;
  return Array.isArray(series) ? series : [];
};

/**
 * Uma barra por mês do período escolhido, na ordem do período — não na ordem em que a
 * série chegou. Um mês sem valor na curva entra como zero: a lacuna no meio de um
 * período contínuo é informação (naquele mês não se desembolsa nada), sumir com a
 * barra encostaria meses não vizinhos e mentiria sobre o intervalo.
 *
 * Sem período legível, a série inteira, ordenada — é o que a tela mostrava antes de
 * existir seletor, e continua sendo o melhor palpite.
 */
const seriesForPeriod = (project, filterDates) => {
  const series = fullProjectAmpliSeries(project);
  const months = monthsBetween(filterDates?.beginAt, filterDates?.endAt);
  const byMonth = new Map(series.map((point) => [point.date, point]));
  if (months.length === 0) {
    return [...series].sort((left, right) =>
      String(left.date).slice(3) + String(left.date).slice(0, 2) <
      String(right.date).slice(3) + String(right.date).slice(0, 2)
        ? -1
        : 1,
    );
  }
  return months.map((date) => ({ date, value: byMonth.get(date)?.value ?? 0 }));
};

// Os callbacks do chart.js (ticks/tooltip) rodam FORA do render do React, então
// `formatNumberToCurrency` — que chama `useProjectContext` por dentro — explodiria ali
// com "Invalid hook call" (erro #321). Formatador puro, sem contexto nenhum: a série já
// vem em reais (`getDetail`/`getFollowUps` dividem por 100 no servidor).
const formatReais = (value) =>
  (Number(value) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const AmpliCurveChart = ({ project }) => {
  const { filterDates } = useProjectContext();
  const series = useMemo(
    () => seriesForPeriod(project, filterDates),
    [project, filterDates],
  );

  if (!isAmpliDisbursementPolicy(project?.cashFlowPlanning)) return null;

  const curve = ampliCurveState(project);
  const detail = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : STATE_DETAILS[curve.status] ?? null;
  const hasSeries = series.length > 0;

  const data = {
    labels: series.map((point) => point.date),
    datasets: [
      {
        label: "Desembolso Ampli (R$)",
        data: series.map((point) => Number(point.value) || 0),
        backgroundColor: "#2563EB",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      // A plataforma registra `chartjs-plugin-datalabels` globalmente (ver
      // RulePreviewChart.jsx) — sem isto, ele desenha o valor cru por cima de cada
      // barra, sobrepondo o eixo e os rótulos de mês.
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => formatReais(context.parsed.y),
        },
      },
    },
    scales: {
      y: {
        ticks: { callback: (value) => formatReais(value) },
      },
    },
  };

  return (
    <section className={`ampli-curve-chart ampli-curve-chart--${curve.status}`}>
      <h2>Desembolso Ampli</h2>
      <p className="card-label">{ampliCurveStateLabel(curve.status)}</p>
      {detail ? <p>{detail}</p> : null}
      {hasSeries ? (
        <div className="ampli-curve-chart__canvas">
          <Bar
            data={data}
            options={options}
            aria-label="Gráfico do desembolso mensal da obra"
          />
        </div>
      ) : (
        <p className="empty-state">
          Nenhuma curva de desembolso para desenhar.
        </p>
      )}
    </section>
  );
};

export default AmpliCurveChart;
